// Pitch Pack v2 wizard — API service for sessions and PPTX generation.
import axios from '@/api/provider';
import { handleError } from '@/api/response';

// ── Sessions ───────────────────────────────────────────────────────────────

const createSession = async ({ companyId, runId = null, pitchId = null }) =>
  axios
    .post('pitch-pack/sessions', {
      company_id: companyId,
      run_id: runId,
      pitch_id: pitchId,
    })
    .then((r) => ({ success: true, session: r }))
    .catch((error) => {
      console.error('[PitchPack] createSession failed:', error);
      return { success: false, error: error?.message || 'Create failed' };
    });

const listSessions = async ({ companyId = null, status = null, limit = 50 } = {}) => {
  const params = { limit };
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return axios
    .get('pitch-pack/sessions', { params })
    .then((r) => ({ success: true, items: r.items || [], count: r.count || 0 }))
    .catch((error) => {
      console.error('[PitchPack] listSessions failed:', error);
      return { success: false, items: [], count: 0 };
    });
};

const getSession = async (sessionId) =>
  axios
    .get(`pitch-pack/sessions/${sessionId}`)
    .then((r) => ({ success: true, session: r }))
    .catch((error) => {
      console.error('[PitchPack] getSession failed:', error);
      return { success: false, session: null };
    });

const updateStep = async (sessionId, step, data) =>
  axios
    .patch(`pitch-pack/sessions/${sessionId}/steps/${step}`, { data })
    .then((r) => ({ success: true, session: r }))
    .catch((error) => {
      console.error('[PitchPack] updateStep failed:', error);
      return { success: false, error: error?.message || 'Save failed' };
    });

const generate = async (sessionId) =>
  axios
    .post(`pitch-pack/sessions/${sessionId}/generate`)
    .then((r) => ({ success: true, session: r }))
    .catch((error) => {
      console.error('[PitchPack] generate failed:', error);
      return { success: false, error: error?.message || 'Generate failed' };
    });

// Per-slide build progress for the Client Pack filmstrip. Every planned slide
// is listed from the start; a state only changes when the backend builder
// reports that slide.
const getProgress = async (sessionId) =>
  axios
    .get(`pitch-pack/sessions/${sessionId}/progress`)
    .then((r) => ({ success: true, progress: r }))
    .catch((error) => {
      console.error('[PitchPack] getProgress failed:', error);
      // handleError rejects with error.RESPONSE, so the status is on the
      // caught value itself — reading error.response.status gave undefined,
      // and the tracker only gives up on 404/403, so a dead session polled
      // forever behind a frozen "Starting the build".
      return { success: false, progress: null, status: error?.status };
    });

/**
 * Download the generated PPTX as a Blob.
 * Bypasses the default get() wrapper so the binary body survives as a Blob.
 */
const download = async (sessionId) => {
  const instance = axios.getAxiosInstance();
  const config = axios.getConfig();
  try {
    const response = await instance.get(`pitch-pack/sessions/${sessionId}/download`, {
      ...config,
      responseType: 'blob',
      timeout: 180000,
    });
    // Extract filename from Content-Disposition header, fall back to X-Filename
    let filename = null;
    const disposition = response.headers?.['content-disposition'];
    if (disposition) {
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1].trim();
    }
    if (!filename) {
      filename = response.headers?.['x-filename'] || null;
    }
    return { success: true, blob: response.data, filename };
  } catch (e) {
    return handleError(e);
  }
};

// ── Market Overview (industry synthesis) ──────────────────────────────────

const triggerMarketOverview = async (sessionId) =>
  axios
    .post(`pitch-pack/sessions/${sessionId}/market-overview`, {})
    .then((r) => ({ success: true, ...r }))
    .catch((error) => {
      // Same unwrap as getProgress: the caught value IS the response, so
      // error.response.status was undefined — is409 could never be true and a
      // "market overview already running" 409 was reported as a success.
      const detail = error?.data?.detail || error?.message || 'Trigger failed';
      const is409 = error?.status === 409;
      return { success: !is409, alreadyRunning: is409, error: detail };
    });

const getMarketOverviewStatus = async (sessionId) =>
  axios
    .get(`pitch-pack/sessions/${sessionId}/market-overview`)
    .then((r) => ({ success: true, ...r }))
    .catch((error) => {
      console.error('[PitchPack] getMarketOverviewStatus failed:', error);
      return { success: false, status: 'none', has_data: false };
    });

export default {
  createSession,
  listSessions,
  getSession,
  updateStep,
  generate,
  getProgress,
  download,
  triggerMarketOverview,
  getMarketOverviewStatus,
};
